/**
 * 프로젝트 동기화 매니저
 * 주기적 동기화, 온/오프라인 감지, 오프라인 캐시, 자동 재동기화 지원
 */

import api from '@/lib/api'

/** 동기화 API 응답 타입 */
interface SyncResponse {
  projects: Array<{
    id: string
    name: string
    updatedAt: string
    [key: string]: unknown
  }>
  syncedAt: string
}

/** 오프라인 캐시 항목 타입 */
interface OfflineCacheEntry {
  timestamp: number
  data: unknown
}

/** 로컬 스토리지 키 */
const STORAGE_KEY_LAST_SYNC = 'video-editor-last-sync-at'
const STORAGE_KEY_OFFLINE_CACHE = 'video-editor-offline-cache'

/** 기본 동기화 간격 (5분) */
const DEFAULT_SYNC_INTERVAL = 5 * 60 * 1000

/**
 * SyncManager 클래스
 * 프로젝트 데이터의 주기적 동기화 및 온/오프라인 전환 처리
 */
class SyncManager {
  /** 동기화 타이머 ID */
  private syncTimer: ReturnType<typeof setInterval> | null = null

  /** 동기화 진행 중 여부 */
  private isSyncing = false

  /** 동기화 완료 콜백 (인자 선택적) */
  onSyncComplete: ((...args: unknown[]) => void) | null = null

  /** 온라인 상태 변경 이벤트 바인딩 해제 함수 */
  private cleanupListeners: (() => void) | null = null

  constructor() {
    // 브라우저 환경에서만 온/오프라인 이벤트 리스너 등록
    if (typeof window !== 'undefined') {
      const handleOnline = () => {
        this.handleOnlineStatusChange(true)
      }
      const handleOffline = () => {
        this.handleOnlineStatusChange(false)
      }

      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)

      this.cleanupListeners = () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }

  /**
   * 주기적 동기화 시작
   * @param intervalMs 동기화 간격 (밀리초, 기본값 5분)
   */
  startSync(intervalMs: number = DEFAULT_SYNC_INTERVAL): void {
    // 이미 실행 중이면 중지 후 재시작
    this.stopSync()

    // 즉시 한 번 실행
    this.syncNow()

    // 주기적 실행 설정
    this.syncTimer = setInterval(() => {
      this.syncNow()
    }, intervalMs)
  }

  /** 주기적 동기화 중지 */
  stopSync(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer)
      this.syncTimer = null
    }
  }

  /** 즉시 동기화 실행 */
  async syncNow(): Promise<SyncResponse | null> {
    // 이미 동기화 중이면 건너뜀
    if (this.isSyncing) return null

    // 오프라인이면 로컬 캐시에 저장하고 종료
    if (!this.isOnline()) {
      this.cacheOfflineChanges()
      return null
    }

    this.isSyncing = true

    try {
      const lastSyncAt = this.getLastSyncAt()
      const response = await api.post<SyncResponse>('/mobile/sync', {
        lastSyncAt,
      })

      // 동기화 시각 저장
      this.setLastSyncAt(response.syncedAt)

      // 오프라인 캐시 정리 (성공적으로 동기화됨)
      this.clearOfflineCache()

      // 콜백 호출
      if (this.onSyncComplete) {
        this.onSyncComplete(response)
      }

      return response
    } catch {
      // 동기화 실패 시 오프라인 캐시에 기록
      this.cacheOfflineChanges()
      return null
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * 마지막 동기화 시각 조회
   * @returns ISO 날짜 문자열 또는 null
   */
  getLastSyncAt(): string | null {
    if (typeof window === 'undefined') return null
    try {
      return localStorage.getItem(STORAGE_KEY_LAST_SYNC)
    } catch {
      return null
    }
  }

  /**
   * 현재 온라인 상태 확인
   * @returns 온라인 여부
   */
  isOnline(): boolean {
    if (typeof navigator === 'undefined') return true
    return navigator.onLine
  }

  /** 이벤트 리스너 및 타이머 정리 */
  destroy(): void {
    this.stopSync()
    if (this.cleanupListeners) {
      this.cleanupListeners()
      this.cleanupListeners = null
    }
    this.onSyncComplete = null
  }

  // ---- 내부 메서드 ----

  /** 마지막 동기화 시각 저장 */
  private setLastSyncAt(syncedAt: string): void {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(STORAGE_KEY_LAST_SYNC, syncedAt)
    } catch {
      // localStorage 용량 초과 등의 경우 무시
    }
  }

  /**
   * 온/오프라인 상태 변경 처리
   * 온라인 복귀 시 자동으로 동기화 트리거
   */
  private handleOnlineStatusChange(isOnline: boolean): void {
    if (isOnline) {
      // 온라인 복귀 시: 오프라인 캐시가 있으면 즉시 동기화
      const cache = this.getOfflineCache()
      if (cache.length > 0) {
        this.syncNow()
      }
    }
  }

  /** 오프라인 변경사항을 로컬 스토리지에 캐시 */
  private cacheOfflineChanges(): void {
    if (typeof window === 'undefined') return

    try {
      const entry: OfflineCacheEntry = {
        timestamp: Date.now(),
        data: { pendingSync: true },
      }

      const existing = this.getOfflineCache()
      existing.push(entry)

      localStorage.setItem(STORAGE_KEY_OFFLINE_CACHE, JSON.stringify(existing))
    } catch {
      // localStorage 용량 초과 등의 경우 무시
    }
  }

  /** 오프라인 캐시 조회 */
  private getOfflineCache(): OfflineCacheEntry[] {
    if (typeof window === 'undefined') return []

    try {
      const raw = localStorage.getItem(STORAGE_KEY_OFFLINE_CACHE)
      if (!raw) return []
      return JSON.parse(raw) as OfflineCacheEntry[]
    } catch {
      return []
    }
  }

  /** 오프라인 캐시 정리 */
  private clearOfflineCache(): void {
    if (typeof window === 'undefined') return

    try {
      localStorage.removeItem(STORAGE_KEY_OFFLINE_CACHE)
    } catch {
      // 무시
    }
  }
}

/** 싱글톤 인스턴스 */
export const syncManager = new SyncManager()
export default SyncManager
